import { useState } from "react";
import { DevicePicker } from "./DevicePicker.js";
import { OverlayDefaultsPanel } from "./OverlayDefaultsPanel.js";
import { SetupPane, type SetupPaneProps } from "./SetupPane.js";
import { ShortcutPanel } from "./ShortcutPanel.js";
import { VoicePanel } from "./VoicePanel.js";

/**
 * The screens the settings card is divided into.
 *
 * Exported for `test/settings-tabs.test.ts`, which holds the division rather
 * than the labels: four tabs, the document first, and the keys and the voice
 * commands sharing the last of them.
 */
export const SETTINGS_TABS = [
  { id: "script", label: "Script" },
  { id: "prompter", label: "Prompter" },
  { id: "devices", label: "Devices" },
  { id: "shortcuts", label: "Shortcuts" },
] as const;

type TabId = (typeof SETTINGS_TABS)[number]["id"];

/**
 * The settings screens (ticket 16, FR-61 to FR-64), tabbed rather than stacked.
 *
 * They are tabs because of what they are for: most are things you set once and
 * then never look at, and stacked they would push the things you *do* use during
 * a take off the bottom of the window. Only the selected tab is mounted, which
 * also means a rebinding capture cannot be left open on a tab you have navigated
 * away from — see `ShortcutPanel`.
 *
 * **The card lives in a modal now** (one-window-polish ticket 05) rather than
 * being one pane of three. That is what the `Script` tab is for and why it is
 * both first in the strip and the one selected on open: with no tab strip on the
 * window, this is the only route back to the URL box once a document is loaded,
 * and "which document am I reading?" is the question you come in here with more
 * often than "how big is the text?".
 */
export function SettingsPanel({
  disabled,
  setup,
}: {
  /** True while a take is in progress. Passed to the devices screen, which is the
   * only one where changing something mid-take has no safe outcome. */
  disabled: boolean;
  /** The setup pane's props, passed straight through. The Script tab renders the
   * *same* component the window renders when nothing is loaded, from the same
   * props — one setup pane, so the two places it appears cannot drift apart. */
  setup: SetupPaneProps;
}): JSX.Element {
  const [tab, setTab] = useState<TabId>("script");

  return (
    <section className="settings">
      <div className="tab-strip" role="tablist" aria-label="Settings">
        {SETTINGS_TABS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={`tab-button${tab === id ? " active" : ""}`}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Where the script comes from, and the only way back to the URL box once
        * one is loaded. `{...setup}` rather than a wrapper of its own: a second
        * copy of this form is exactly what would drift from the one the window
        * shows on an empty launch. A load that works closes the modal, which is
        * the shell's doing — see `AppShell`. */}
      {tab === "script" && <SetupPane {...setup} />}

      {tab === "prompter" && <OverlayDefaultsPanel />}
      {/* Locked mid-take for the same reason refresh is: swapping hardware under
        * a running capture has no safe outcome. */}
      {tab === "devices" && <DevicePicker disabled={disabled} />}
      {/* One tab, two screens: the keys (FR-52) and the voice commands (ticket
        * 15, FR-54 to FR-56). They are together because they answer the same
        * question — how do I tell cuekards to do something without touching it? —
        * and the difference between them is which widgets that takes, a
        * rebinding table on one side and a switch with a phrase list on the
        * other, which is not a difference the user came here with. Neither is a
        * screen's worth alone.
        *
        * Both sit here rather than beside speech-following, even though the
        * voice half shares its microphone: the switch and its phrase list are
        * something you read once and set, not something you work with while
        * filming.
        *
        * Not locked mid-take. A capture swallows one keypress from the shell's
        * listener, which is a poor moment to choose while rolling — but the
        * transport buttons are untouched, and forbidding it outright would be
        * the wrong trade for the one case where a key turns out to be wrong and
        * you need it changed now. */}
      {tab === "shortcuts" && (
        <>
          <ShortcutPanel host="desktop" />
          <VoicePanel />
        </>
      )}
    </section>
  );
}
